import { Address } from '../../domain/Address'
import { AddressDTO } from './AddressDTO'
import { Answer } from '../../domain/Answer'
import { AnswerDTO } from './AnswerDTO'
import { Area } from '../../domain/Area'
import { AreaAvailability } from '../../domain/AreaAvailability'
import { AreaDTO } from './AreaDTO'
import { Availability } from '../../domain/Availability'
import { AvailabilityDTO } from './AvailabilityDTO'
import { BookingDetails } from '../../domain/BookingDetails'
import { BookingDetailsDTO } from './BookingDetailsDTO'
import { BookingFeedback } from '../../domain/BookingFeedback'
import { BookingFeedbackDTO } from './BookingFeedbackDTO'
import { BookingReason } from '../../domain/BookingReason'
import { BookingReasonDTO } from './BookingReasonDTO'
import { BookingRequest } from '../../domain/BookingRequest'
import { CancellationReason } from '../../domain/CancellationReason'
import { CancellationReasonDTO } from './CancellationReasonDTO'
import { BookingRequestDTO } from './BookingRequestDTO'
import { BookingSlot } from '../../domain/BookingSlot'
import { BookingSlotAvailability } from '../../domain/BookingSlotAvailability'
import { BookingSlotDTO } from './BookingSlotDTO'
import { BookingSlotOption } from '../../domain/BookingSlotOption'
import { BookingStatusType } from '../../domain/BookingStatus'
import { BookingStatusTypeDTO } from './BookingStatusTypeDTO'
import { ContactDetailsDTO } from './ContactDetailsDTO'
import { DiaryNote } from '../../domain/DiaryNote'
import { DiaryNoteDTO } from './DiaryNoteDTO'
import { Event, EventException, EventRecurrence, MonthlyRepetitionType } from '../../domain/Event'
import { EventDTO, EventRecurrenceDTO, MonthlyRepetitionTypeDTO } from './EventDTO'
import { Injectable } from '@angular/core'
import { Period } from '../../domain/Period'
import { PeriodDTO } from './PeriodDTO'
import { Question, QuestionType } from '../../domain/Question'
import { QuestionDTO, QuestionTypeDTO } from './QuestionDTO'
import { ReasonDateRangeFilter } from '../../domain/ReasonDateRangeFilter'
import { ReasonDateRangeFilterDTO } from './ReasonDateRangeFilterDTO'
import { ReasonScheduleRule } from '../../domain/ReasonScheduleRule'
import { ReasonScheduleRuleDTO } from './ReasonScheduleRuleDTO'
import { Table } from '../../domain/Table'
import { TableDTO } from './TableDTO'
import { Time } from '../../domain/Time'
import { Venue } from '../../domain/Venue'
import { VenueDTO } from './VenueDTO'
import { Voucher } from '../../domain/Voucher'
import { VoucherDTO } from './VoucherDTO'
import { SKUDTO } from './SKUDTO'
import { SKU } from '../../domain/SKU'
import { VoucherSKUValue } from '../../domain/VoucherSKUValue'
import { VoucherSKUValueDTO } from './VoucherSKUValueDTO'
import { Sale, SalePurpose } from '../../domain/Sale'
import { SaleDTO } from './SaleDTO'
import { Payment } from '../../domain/Payment'
import { PaymentDTO } from './PaymentDTO'
import { Order } from '../../domain/Order'
import { OrderDTO } from './OrderDTO'
import { LineItem } from '../../domain/LineItem'
import { LineItemDTO } from './LineItemDTO'
import { DeliveryDetails } from '../../domain/DeliveryDetails'
import { DeliveryDetailsDTO } from './DeliveryDetailsDTO'
import { PaymentsAccount } from '../../domain/PaymentsAccount'
import { PaymentsAccountDTO } from './PaymentsAccountDTO'
import { OpeningHours, OpeningHoursDay } from '../../domain/OpeningHours'
import { Consumable, Menu, MenuItem, MenuCourse } from '../../domain/Consumable'
import { ConsumableDTO, MenuDTO, MenuItemDTO, MenuCourseDTO } from './ConsumableDTO'
import { MenuLinkType } from '../../domain/MenuLinkType'
import { MenuLinkTypeDTO } from './MenuLinkTypeDTO'
import { ProductLibraryDTO } from './ProductLibraryDTO'
import { ModifierDTO, ModifierGroupDTO } from './ModifierDTO'
import { Modifier, ModifierGroup } from '../../domain/Modifier'
import { ProductLibrary } from '../../domain/ProductLibrary'
import { BookingDocket, CoverMenuChoice, CoverProductSelection } from '../../domain/BookingDocket'
import { BookingDocketDTO, CoverMenuChoiceDTO, CoverProductSelectionDTO } from './BookingDocketDTO'

@Injectable({
    providedIn: 'root',
})
export class DTOAdapter {

    adaptAvailabilityDto(dto: AvailabilityDTO): Availability {
        const areas = dto.areas.map(dto => {
            const area = this.adaptAreaDto(dto.area)
            const bookingSlots = dto.bookingSlots.map(dto => {
                return new BookingSlotAvailability(
                    new BookingSlot(new Date(dto.bookingSlot.dateTime)),
                    dto.options.map(dto => {
                        return new BookingSlotOption(
                            dto.partySize,
                            dto.durationMinutes,
                            dto.allTableCount,
                            dto.reservableTableCount,
                            dto.reasonId,
                            dto.eventId,
                            dto.cancellationChargeAmount,
                            dto.cancellationChargeIsPerCover,
                            dto.cancellationChargeCutOffHours,
                            dto.depositAmount,
                            dto.depositAmountIsPerCover,
                            dto.depositRefundCutOffHours
                        )
                    })
                )
            })
            return new AreaAvailability(area, bookingSlots, dto.exceptionDescription)
        })
        const venue = this.adaptVenueDto(dto.venue)
        const productLibrary = dto.productLibrary ? this.adaptProductLibraryDto(dto.productLibrary) : null
        const publicDiaryNotes = dto.publicDiaryNotes.map(noteDto => this.adaptDiaryNoteDto(noteDto))
        return new Availability(areas, venue, productLibrary, publicDiaryNotes)
    }

    adaptDiaryNoteDto(dto: DiaryNoteDTO): DiaryNote {
        return new DiaryNote(
            dto.id,
            new Date(dto.startDate),
            new Date(dto.endDate),
            dto.note
        )
    }

    adaptVenueDto(dto: VenueDTO): Venue {
        const reasons = dto.reasons.map(dto => this.adaptBookingReasonDto(dto))
        const questions = dto.questions.map(dto => this.adaptQuestionDto(dto))
        const events = dto.events.map(dto => this.adaptEventDto(dto))
        const paymentsAccount = dto.paymentsAccount ? new PaymentsAccount(
            dto.paymentsAccount.id,
            dto.paymentsAccount.gateway,
            dto.paymentsAccount.applePayEnabled,
            dto.paymentsAccount.capabilities
        ) : null
        return new Venue(
            dto.id,
            dto.venueSlug,
            dto.displayName,
            dto.phoneNumber,
            dto.emailAddress,
            this.adaptAddressDto(dto.address),
            dto.bookingInterval,
            dto.minLargePartySize,
            dto.largePartyMessage,
            dto.noBookingSlotAvailableMessage,
            dto.shortPreBookingWindowMessage,
            dto.dogFriendlyNoAvailabilityMessage,
            dto.bookingsPausedNoAvailabilityMessage,
            dto.venueClosedNoAvailabilityMessage,
            dto.currencyCode,
            dto.organisationId,
            dto.businessId,
            [...(dto.entitlements ?? [])],
            paymentsAccount,
            dto.cancellationChargeAmount,
            dto.cancellationChargeCutOffHours,
            dto.depositAmount,
            dto.depositRefundCutOffDays,
            dto.depositRefundPolicy,
            dto.phoneNumberRequired,
            dto.primaryColour,
            dto.pinnedEventId,
            dto.dogFriendlyQuestionEnabled,
            dto.wheelchairAccessibleQuestionEnabled,
            dto.pramAccessibleQuestionEnabled,
            dto.highchairFriendlyQuestionEnabled,
            dto.customerCanChooseArea ?? (dto.areaBookingOrder.length === 0),
            [...dto.areaBookingOrder],
            dto.bookingsClosedUntilDate ? new Date(dto.bookingsClosedUntilDate) : null,
            dto.bookingPolicy ?? null,
            dto.largePartyBookingPolicy ?? null,
            dto.largePartyBookingPolicyMinimumPartySize ?? null,
            dto.hidesClosedBookingSlots,
            dto.enquiriesEnabled,
            dto.waitlistsEnabled,
            dto.voucherLogoImageId,
            reasons,
            questions,
            events,
            dto.description ?? null,
            [...(dto.promotionalImageIds ?? [])],
            dto.openingHours === null || dto.openingHours === undefined
                ? null
                : new OpeningHours(
                    dto.openingHours.days.map(day => new OpeningHoursDay(
                        day.dayOfWeek,
                        day.periods.map(period => this.adaptPeriodDto(period))
                    ))
                ),
            dto.containsCustomersRequiringDeposit ?? false,
            dto.businessDisplayName ?? null,
            dto.privacyPolicy ?? null,
            dto.preorderMenuId ?? null,
            dto.paymentsAccountAssignments === undefined ? undefined : Object.fromEntries(
                Object.entries(dto.paymentsAccountAssignments).map(([capability, account]) => [
                    capability, new PaymentsAccount(account.id, account.gateway, account.applePayEnabled, account.capabilities),
                ])
            )
        )
    }

    adaptBookingReasonDto(dto: BookingReasonDTO): BookingReason {
        return new BookingReason(
            dto.id,
            dto.displayName,
            dto.displayOrder,
            dto.description,
            dto.schedule.map(dto => this.adaptReasonScheduleRuleDto(dto)),
            dto.dateRangeFilters.map(dto => this.adaptReasonDateRangeFilterDto(dto)),
            dto.areaBookingOrder?.length ? [...dto.areaBookingOrder] : null,
            dto.diningInformation,
            dto.maximumPartySize,
            dto.linkExclusive,
            dto.dailyCoverLimit,
            dto.largePartyNoAvailabilityMessage,
            dto.preorderMenuId,
            dto.displayedOpeningTimes,
            dto.imageId ?? null,
            dto.isActive ?? true
        )
    }

    adaptCancellationReasonDto(dto: CancellationReasonDTO): CancellationReason {
        return new CancellationReason(
            dto.id,
            dto.displayName,
            dto.orderIndex,
            new Date(dto.dateCreated),
            new Date(dto.dateUpdated),
            dto.isPublic ?? true
        )
    }

    adaptCancellationReason(reason: CancellationReason): CancellationReasonDTO {
        return new CancellationReasonDTO(
            reason.id,
            reason.displayName,
            reason.orderIndex,
            reason.dateCreated.toISOString(),
            reason.dateUpdated.toISOString(),
            reason.isPublic
        )
    }

    adaptReasonScheduleRuleDto(dto: ReasonScheduleRuleDTO): ReasonScheduleRule {
        return new ReasonScheduleRule(
            dto.id,
            dto.daysOfWeek,
            dto.period ? this.adaptPeriodDto(dto.period) : null
        )
    }

    adaptReasonDateRangeFilterDto(dto: ReasonDateRangeFilterDTO): ReasonDateRangeFilter {
        return new ReasonDateRangeFilter(
            dto.id,
            new Date(dto.startDate),
            new Date(dto.endDate)
        )
    }

    adaptQuestionDto(dto: QuestionDTO): Question {
        return new Question(
            dto.id,
            dto.text,
            dto.required,
            this.adaptQuestionTypeDto(dto.type),
            [...dto.options],
            [...dto.reasonIds],
            dto.active ?? true
        )
    }

    adaptQuestionType(type: QuestionType): QuestionTypeDTO {
        switch (type) {
        case QuestionType.Checkbox:
            return QuestionTypeDTO.Checkbox
        case QuestionType.Dropdown:
            return QuestionTypeDTO.Dropdown
        case QuestionType.AcknowledgeEndTime:
            return QuestionTypeDTO.AcknowledgeEndTime
        case QuestionType.DOB:
            return QuestionTypeDTO.DOB
        case QuestionType.ReasonsForVisit:
            return QuestionTypeDTO.ReasonsForVisit
        case QuestionType.DietaryRestrictions:
            return QuestionTypeDTO.DietaryRestrictions
        case QuestionType.Text:
            return QuestionTypeDTO.Text
        case QuestionType.DietaryRestrictionsText:
            return QuestionTypeDTO.DietaryRestrictionsText
        }
    }

    adaptQuestionTypeDto(dto: QuestionTypeDTO): QuestionType {
        switch (dto) {
        case QuestionTypeDTO.Checkbox:
            return QuestionType.Checkbox
        case QuestionTypeDTO.Dropdown:
            return QuestionType.Dropdown
        case QuestionTypeDTO.AcknowledgeEndTime:
            return QuestionType.AcknowledgeEndTime
        case QuestionTypeDTO.DOB:
            return QuestionType.DOB
        case QuestionTypeDTO.ReasonsForVisit:
            return QuestionType.ReasonsForVisit
        case QuestionTypeDTO.DietaryRestrictions:
            return QuestionType.DietaryRestrictions
        case QuestionTypeDTO.Text:
            return QuestionType.Text
        case QuestionTypeDTO.DietaryRestrictionsText:
            return QuestionType.DietaryRestrictionsText
        }
    }

    adaptAreaDto(dto: AreaDTO): Area {
        return new Area(
            dto.id,
            dto.displayName,
            dto.description,
            dto.displayOrder,
            dto.preBookingWindowMinutes,
            new Date(dto.cutOffTime),
            dto.tables.map(dto => this.adaptTableDto(dto))
        )
    }

    adaptAddressDto(dto: AddressDTO): Address {
        return new Address(
            dto.addressLineOne,
            dto.addressLineTwo,
            dto.city,
            dto.county,
            dto.postCode,
            dto.country
        )
    }

    adaptTableDto(dto: TableDTO): Table {
        return new Table(
            dto.reservable,
            dto.minimumSeats,
            dto.maximumSeats,
            dto.wheelchairAccessible,
            dto.dogFriendly,
            dto.pramAccessible,
            dto.highchairFriendly
        )
    }

    adaptEventDto(dto: EventDTO): Event {
        return new Event(
            dto.id,
            dto.displayName,
            dto.displayOrder,
            dto.description,
            dto.imageId,
            dto.cancellationChargeAmount,
            dto.cancellationChargePartyAmount,
            dto.cancellationChargeCutOffHours,
            dto.depositAmount,
            dto.depositFlatFeeAmount,
            dto.depositRefundCutOffDays,
            dto.isExclusive,
            [...dto.questionIds],
            dto.notReservableOnline,
            dto.notReservableOnlineMessage,
            this.adaptEventRecurrenceDto(dto.recurrence),
            dto.partySizeRestriction ? [...dto.partySizeRestriction] : null,
            dto.partySizeRestrictedMessage,
            dto.dogFriendlyNotAllowed,
            dto.minLargePartySize,
            [...dto.tableIds]
        )
    }

    adaptEventRecurrenceDto(dto: EventRecurrenceDTO): EventRecurrence {
        const exceptions = (dto.exceptions ?? []).map(dto => {
            return new EventException(
                dto.id,
                this.adaptLocalDateDto(dto.date)
            )
        })
        return new EventRecurrence(
            dto.startDates.map(dto => this.adaptLocalDateDto(dto)),
            dto.dateComponent ?? null,
            dto.weeklyRepeatsOnDaysOfWeek ? [...dto.weeklyRepeatsOnDaysOfWeek] : null,
            dto.monthlyRepetitionType !== null && dto.monthlyRepetitionType !== undefined
                ? this.adaptMonthlyRepetitionTypeDto(dto.monthlyRepetitionType)
                : null,
            exceptions,
            dto.endDate ? this.adaptLocalDateDto(dto.endDate) : null,
            dto.startTime ? new Time(dto.startTime, null, null) : null,
            dto.endTime ? new Time(dto.endTime, null, null) : null
        )
    }

    private adaptMonthlyRepetitionTypeDto(dto: MonthlyRepetitionTypeDTO): MonthlyRepetitionType {
        switch (dto) {
        case MonthlyRepetitionTypeDTO.DAY_OF_MONTH:
            return MonthlyRepetitionType.DayOfMonth
        case MonthlyRepetitionTypeDTO.DAY_OF_WEEK:
            return MonthlyRepetitionType.DayOfWeek
        }
    }

    private adaptLocalDateDto(dto: string): Date {
        const [year, month, day] = dto.split('-').map(value => Number(value))
        if (year && month && day) {
            return new Date(year, month - 1, day)
        }
        return new Date(dto)
    }

    adaptBookingDetails(details: BookingDetails): BookingDetailsDTO {
        return new BookingDetailsDTO(
            details.bookingId,
            details.currencyCode,
            details.venueId,
            details.venueName,
            this.adaptAddressDto(details.venueAddress),
            details.venueDiningInformation,
            details.venueEmailAddress,
            details.venuePhoneNumber,
            details.bookingName,
            details.bookingEmailAddress,
            details.partySize,
            details.durationMinutes,
            this.makeLocalISOFormattedDateTimeString(details.dateTime),
            this.adaptBookingStatusType(details.statusType),
            details.cancellationChargeAmount,
            details.cancellationChargeCutOffTime?.toISOString() ?? null,
            details.areaId,
            details.areaName,
            details.reasonId,
            details.reasonName,
            details.reasonDiningInformation,
            details.reasonIsLinkExclusive,
            details.eventId,
            details.eventName,
            details.eventImageUrl,
            details.depositAmount,
            details.depositAmountPaid,
            details.depositAmountOutstanding,
            details.depositCurrencyCode,
            details.depositDatePaid ? details.depositDatePaid.toISOString() : null,
            details.depositDateRefunded ? details.depositDateRefunded.toISOString() : null,
            details.depositRefundCutOffTime ? details.depositRefundCutOffTime.toISOString() : null,
            details.depositRefundPolicy ? details.depositRefundPolicy : null,
            details.requiresWheelchairAccess,
            details.requiresDogFriendly,
            details.requiresPramAccessible,
            details.requiresHighchairFriendly,
            details.modifyBookingPath ? details.modifyBookingPath : null,
            details.pendingPaymentExpiryDateTime
                ? this.makeLocalISOFormattedDateTimeString(details.pendingPaymentExpiryDateTime)
                : null,
            details.docket ? this.adaptBookingDocket(details.docket) : null,
            details.cancellationReasons.map(r => this.adaptCancellationReason(r)),
            details.order ? this.adaptOrder(details.order) : null,
            details.additionalBookings.map(booking => this.adaptBookingDetails(booking)),
            details.sale ? {
                ...details.sale,
                order: this.adaptOrder(details.sale.order),
                payment: details.sale.payment ? {
                    ...details.sale.payment,
                    dateCreated: details.sale.payment.dateCreated.toISOString(),
                    datePaid: details.sale.payment.datePaid?.toISOString() ?? null,
                    refunds: details.sale.payment.refunds.map(refund => ({ ...refund, dateCreated: refund.dateCreated.toISOString() })),
                } : null,
            } : null,
            details.orderRefundableLineIds,
            details.cancellationAmountCharged,
            details.cancellationChargeAutomatically,
            details.depositAmountRefundable
        )
    }

    adaptBookingDetailsDto(dto: BookingDetailsDTO): BookingDetails {
        return new BookingDetails(
            dto.bookingId,
            dto.currencyCode,
            dto.venueId,
            dto.venueName,
            this.adaptAddressDto(dto.venueAddress),
            dto.venueDiningInformation,
            dto.venueEmailAddress,
            dto.venuePhoneNumber,
            dto.bookingName,
            dto.bookingEmailAddress,
            dto.partySize,
            dto.durationMinutes,
            new Date(dto.dateTime),
            this.adaptBookingStatusTypeDto(dto.statusType),
            dto.cancellationChargeAmount,
            dto.cancellationChargeCutOffTime ? new Date(dto.cancellationChargeCutOffTime) : null,
            dto.areaId,
            dto.areaName ? dto.areaName : null,
            dto.reasonId,
            dto.reasonName,
            dto.reasonDiningInformation,
            dto.reasonIsLinkExclusive,
            dto.eventId,
            dto.eventName,
            dto.eventImageUrl,
            dto.depositAmount,
            dto.depositAmountPaid ?? null,
            dto.depositAmountOutstanding ?? null,
            dto.depositCurrencyCode,
            dto.depositDatePaid ? new Date(dto.depositDatePaid) : null,
            dto.depositDateRefunded ? new Date(dto.depositDateRefunded) : null,
            dto.depositRefundCutOffTime ? new Date(dto.depositRefundCutOffTime) : null,
            dto.depositRefundPolicy ? dto.depositRefundPolicy : null,
            dto.requiresWheelchairAccess,
            dto.requiresDogFriendly,
            dto.requiresPramAccessible,
            dto.requiresHighchairFriendly,
            dto.modifyBookingPath ? dto.modifyBookingPath : null,
            dto.pendingPaymentExpiryDateTime ? new Date(dto.pendingPaymentExpiryDateTime) : null,
            dto.docket ? this.adaptBookingDocketDto(dto.docket) : null,
            dto.cancellationReasons.map(r => this.adaptCancellationReasonDto(r)),
            dto.order ? this.adaptOrderDto(dto.order) : null,
            (dto.additionalBookings ?? []).map(booking => this.adaptBookingDetailsDto(booking)),
            dto.sale ? this.adaptSaleDto(dto.sale) : null,
            dto.orderRefundableLineIds ?? [],
            dto.cancellationAmountCharged ?? 0,
            dto.cancellationChargeAutomatically ?? true,
            dto.depositAmountRefundable ?? null
        )
    }

    adaptBookingFeedback(feedback: BookingFeedback): BookingFeedbackDTO {
        return new BookingFeedbackDTO(
            feedback.id,
            feedback.rating,
            feedback.food,
            feedback.service,
            feedback.atmosphere,
            feedback.comment,
            feedback.isRedirectedExternally,
            feedback.dateCreated
        )
    }

    adaptBookingFeedbackDto(dto: BookingFeedbackDTO): BookingFeedback {
        return new BookingFeedback(
            dto.id,
            dto.rating,
            dto.food,
            dto.service,
            dto.atmosphere,
            dto.comment,
            dto.isRedirectedExternally,
            dto.dateCreated
        )
    }

    private adaptBookingStatusType(type: BookingStatusType): BookingStatusTypeDTO {
        switch (type) {
        case BookingStatusType.Booked:
            return BookingStatusTypeDTO.Booked
        case BookingStatusType.NoShow:
            return BookingStatusTypeDTO.NoShow
        case BookingStatusType.Cancelled:
            return BookingStatusTypeDTO.Cancelled
        case BookingStatusType.Seated:
            return BookingStatusTypeDTO.Seated
        case BookingStatusType.Finished:
            return BookingStatusTypeDTO.Finished
        case BookingStatusType.Rejected:
            return BookingStatusTypeDTO.Rejected
        case BookingStatusType.Requested:
            return BookingStatusTypeDTO.Requested
        case BookingStatusType.PendingPayment:
            return BookingStatusTypeDTO.PendingPayment
        }
    }

    private adaptBookingStatusTypeDto(dto: BookingStatusTypeDTO): BookingStatusType {
        switch (dto) {
        case BookingStatusTypeDTO.Booked:
            return BookingStatusType.Booked
        case BookingStatusTypeDTO.NoShow:
            return BookingStatusType.NoShow
        case BookingStatusTypeDTO.Cancelled:
            return BookingStatusType.Cancelled
        case BookingStatusTypeDTO.Seated:
            return BookingStatusType.Seated
        case BookingStatusTypeDTO.Finished:
            return BookingStatusType.Finished
        case BookingStatusTypeDTO.Rejected:
            return BookingStatusType.Rejected
        case BookingStatusTypeDTO.Requested:
            return BookingStatusType.Requested
        case BookingStatusTypeDTO.PendingPayment:
            return BookingStatusType.PendingPayment
        }
    }

    adaptBookingRequest(bookingRequest: BookingRequest): BookingRequestDTO {
        const answers = bookingRequest.answers.map(answer => this.adaptAnswer(answer))
        return new BookingRequestDTO(
            bookingRequest.id,
            new BookingSlotDTO(bookingRequest.bookingSlot.toLocalISOFormattedDateTimeString()),
            bookingRequest.areaId,
            new ContactDetailsDTO(
                bookingRequest.firstName,
                bookingRequest.lastName,
                bookingRequest.emailAddress,
                bookingRequest.phoneNumber
            ),
            bookingRequest.partySize,
            bookingRequest.notes,
            bookingRequest.agreedToMarketing,
            bookingRequest.agreedToTerms,
            bookingRequest.reasonId,
            bookingRequest.eventId,
            bookingRequest.basketItems,
            answers,
            bookingRequest.depositPaymentIntentId,
            bookingRequest.requiresWheelchairAccess,
            bookingRequest.requiresDogFriendly,
            bookingRequest.requiresPramAccessible,
            bookingRequest.requiresHighchairFriendly,
            bookingRequest.utmSource,
            bookingRequest.utmMedium,
            bookingRequest.utmCampaign,
            bookingRequest.durationMinutes,
            (bookingRequest.additionalBookings ?? []).map(item => ({ ...item,
                bookingSlot: this.adaptBookingSlot(item.bookingSlot),
                answers: item.answers.map(answer => this.adaptAnswer(answer)),
            })),
            bookingRequest.agreedToMerchantPrivacy
        )
    }

    adaptBookingRequestDto(dto: BookingRequestDTO): BookingRequest {
        return new BookingRequest(
            dto.id,
            this.adaptBookingSlotDto(dto.bookingSlot),
            dto.areaId,
            dto.contact.firstName,
            dto.contact.lastName,
            dto.contact.emailAddress,
            dto.contact.phoneNumber,
            dto.partySize,
            dto.notes,
            dto.agreedToMarketing,
            dto.agreedToTerms,
            dto.reasonId,
            dto.eventId,
            dto.basketItems,
            dto.answers.map(answer => new Answer(
                new Question(answer.questionId, answer.question, answer.required,
                    this.adaptQuestionTypeDto(answer.type), [], []),
                answer.answer
            )),
            dto.depositPaymentIntentId ?? null,
            dto.requiresWheelchairAccess,
            dto.requiresDogFriendly,
            dto.requiresPramAccessible,
            dto.requiresHighchairFriendly,
            dto.utmSource,
            dto.utmMedium,
            dto.utmCampaign,
            dto.durationMinutes ?? null,
            (dto.additionalBookings ?? []).map(item => ({ ...item,
                bookingSlot: this.adaptBookingSlotDto(item.bookingSlot),
                answers: item.answers.map(answer => new Answer(
                    new Question(answer.questionId, answer.question, answer.required,
                        this.adaptQuestionTypeDto(answer.type), [], []), answer.answer)),
            })),
            dto.agreedToMerchantPrivacy ?? null
        )
    }

    adaptBookingSlotDto(dto: BookingSlotDTO): BookingSlot {
        return new BookingSlot(new Date(dto.dateTime))
    }

    adaptBookingSlot(bookingSlot: BookingSlot): BookingSlotDTO {
        return new BookingSlotDTO(bookingSlot.toLocalISOFormattedDateTimeString())
    }

    adaptPeriod(period: Period): PeriodDTO {
        return new PeriodDTO(
            period.start.toString(),
            period.end.toString(),
            period.open
        )
    }

    adaptPeriodDto(dto: PeriodDTO): Period {
        return new Period(
            new Time(dto.start, null, null),
            new Time(dto.end, null, null),
            dto.open
        )
    }

    adaptVoucherSKUValueDto(dto: VoucherSKUValueDTO): VoucherSKUValue {
        return new VoucherSKUValue(
            dto.id,
            dto.skuId,
            dto.amount,
            new Date(dto.dateCreated),
            new Date(dto.dateUpdated)
        )
    }

    adaptVoucherDto(dto: VoucherDTO): Voucher {
        return new Voucher(
            dto.id,
            dto.displayName,
            dto.description,
            dto.imageId,
            dto.policy,
            dto.expiryMonths ? dto.expiryMonths : null,
            dto.expiryDate ? new Date(dto.expiryDate) : null,
            dto.postalDeliveryFee ?? null,
            dto.skus.map(dto => this.adaptSKUDto(dto)),
            dto.skuValues.map(dto => this.adaptVoucherSKUValueDto(dto))
        )
    }

    adaptSKUDto(dto: SKUDTO): SKU {
        return new SKU(
            dto.id,
            dto.price ?? null,
            dto.onSale
        )
    }

    adaptSaleDto(dto: SaleDTO): Sale {
        return new Sale(
            dto.id,
            this.adaptOrderDto(dto.order),
            dto.deliveryDetails ? this.adapyDeliveryDetailsDto(dto.deliveryDetails) : null,
            new Date(dto.dateCreated),
            dto.payment ? this.adaptPaymentDto(dto.payment) : null,
            dto.purpose ?? SalePurpose.VoucherPurchase
        )
    }

    adaptPaymentDto(dto: PaymentDTO): Payment {
        return new Payment(dto.id, dto.paymentIntentId, new Date(dto.dateCreated),
            dto.datePaid ? new Date(dto.datePaid) : null, dto.amount ?? null,
            (dto.refunds ?? []).map(refund => ({ ...refund, dateCreated: new Date(refund.dateCreated) })), dto.capturedTransactionId ?? null)
    }

    adaptOrder(order: Order): OrderDTO {
        return { ...order, lineItems: order.lineItems.map(line => ({
            ...line, customerRefundableUntil: line.customerRefundableUntil?.toISOString() ?? null,
        })) }
    }

    adaptOrderDto(dto: OrderDTO): Order {
        return new Order(
            dto.id,
            dto.lineItems.map(dto => this.adaptLineItemDto(dto)),
            dto.currencyCode,
            dto.isPaid ?? false
        )
    }

    adaptLineItemDto(dto: LineItemDTO): LineItem {
        return new LineItem(
            dto.id,
            dto.name,
            dto.skuId,
            dto.amount,
            dto.quantity,
            dto.externalReference ?? null,
            dto.modifiers ?? [],
            dto.customerRefundableUntil ? new Date(dto.customerRefundableUntil) : null,
            dto.paidOnline ?? true
        )
    }

    adapyDeliveryDetailsDto(dto: DeliveryDetailsDTO): DeliveryDetails {
        return new DeliveryDetails(
            dto.emailAddress ? dto.emailAddress : null,
            dto.name ? dto.name : null,
            dto.postalDeliveryAddress ? this.adaptAddressDto(dto.postalDeliveryAddress) : null,
        )
    }

    adaptProductLibraryDto(dto: ProductLibraryDTO): ProductLibrary {
        return new ProductLibrary(
            dto.id,
            dto.consumables.map(consumable => this.adaptConsumableDto(consumable)),
            dto.menus.map(menu => this.adaptMenuDto(menu)),
            dto.vouchers.map(dto => this.adaptVoucherDto(dto)),
            dto.modifierGroups.map(g => this.adaptModifierGroupDto(g))
        )
    }

    adaptConsumableDto(dto: ConsumableDTO): Consumable {
        return new Consumable(
            dto.id,
            dto.displayName,
            dto.description,
            dto.skus.map(dto => this.adaptSKUDto(dto)),
            [...(dto.modifierGroupIds || [])],
            dto.imageId ?? null
        )
    }

    adaptModifierGroupDto(dto: ModifierGroupDTO): ModifierGroup {
        return new ModifierGroup(
            dto.id,
            dto.name,
            dto.minimumModifiersRequired,
            dto.maximumModifiersRequired,
            dto.modifiers.map(m => this.adaptModifierDto(m))
        )
    }

    adaptModifierDto(dto: ModifierDTO): Modifier {
        return new Modifier(
            dto.id,
            dto.displayName,
            dto.description,
            dto.skus.map(s => this.adaptSKUDto(s))
        )
    }

    adaptMenuDto(dto: MenuDTO): Menu {
        return new Menu(
            dto.id,
            dto.displayName,
            dto.description,
            this.adaptMenuLinkTypeDto(dto.linkType),
            dto.menuCourses.map(courseDto => this.adaptMenuCourseDto(courseDto)),
            dto.minimumCoursesRequired ?? null,
            dto.maximumCoursesRequired ?? null,
            dto.isPaid ?? false
        )
    }

    adaptMenuCourseDto(dto: MenuCourseDTO): MenuCourse {
        return new MenuCourse(
            dto.id,
            dto.menuId,
            dto.displayName,
            dto.orderIndex,
            dto.minimumItemsRequired,
            dto.maximumItemsRequired,
            dto.menuItems.map(itemDto => this.adaptMenuItemDto(itemDto)),
            dto.excludesFromCourseCount ?? false,
            dto.description ?? null
        )
    }

    adaptMenuItem(menuItem: MenuItem): MenuItemDTO {
        return new MenuItemDTO(
            menuItem.id,
            menuItem.menuId,
            menuItem.menuCourseId,
            menuItem.consumableId,
            menuItem.skuId,
            menuItem.refundable,
            menuItem.paidOnline
        )
    }

    adaptMenuItemDto(dto: MenuItemDTO): MenuItem {
        return new MenuItem(
            dto.id,
            dto.menuId,
            dto.menuCourseId,
            dto.consumableId,
            dto.skuId,
            dto.refundable ?? true,
            dto.paidOnline ?? true
        )
    }

    adaptBookingDocket(docket: BookingDocket): BookingDocketDTO {
        return new BookingDocketDTO(
            docket.id,
            docket.menuId,
            docket.coverChoices.map(choice => this.adaptCoverMenuChoice(choice)),
            docket.dateCreated.toISOString(),
            docket.dateUpdated.toISOString(),
            docket.minimumCoursesRequired,
            docket.maximumCoursesRequired
        )
    }

    adaptBookingDocketDto(dto: BookingDocketDTO): BookingDocket {
        return new BookingDocket(
            dto.id,
            dto.menuId,
            dto.coverChoices.map(choice => this.adaptCoverMenuChoiceDto(choice)),
            new Date(dto.dateCreated),
            new Date(dto.dateUpdated),
            dto.minimumCoursesRequired ?? null,
            dto.maximumCoursesRequired ?? null
        )
    }

    private adaptCoverMenuChoice(choice: CoverMenuChoice): CoverMenuChoiceDTO {
        return new CoverMenuChoiceDTO(
            choice.name ? choice.name : null,
            choice.notes ? choice.notes : null,
            choice.selections.map(sel => this.adaptCoverProductSelection(sel))
        )
    }

    private adaptCoverMenuChoiceDto(dto: CoverMenuChoiceDTO): CoverMenuChoice {
        return new CoverMenuChoice(
            dto.name ? dto.name : null,
            dto.notes ? dto.notes : null,
            dto.selections.map(sel => this.adaptCoverProductSelectionDto(sel))
        )
    }

    private adaptCoverProductSelection(sel: CoverProductSelection): CoverProductSelectionDTO {
        return new CoverProductSelectionDTO(
            sel.productSkuId,
            [...sel.modifierSkuIds],
            sel.menuItemId ?? null
        )
    }

    private adaptCoverProductSelectionDto(dto: CoverProductSelectionDTO): CoverProductSelection {
        return new CoverProductSelection(
            dto.productSkuId,
            [...dto.modifierSkuIds],
            dto.menuItemId ?? null
        )
    }

    private adaptSku(sku: SKU): SKUDTO {
        return new SKUDTO(
            sku.id,
            sku.price,
            sku.onSale
        )
    }

    private adaptAnswer(answer: Answer): AnswerDTO {
        return new AnswerDTO(
            answer.question.id,
            answer.question.text,
            answer.question.required,
            this.adaptQuestionType(answer.question.type),
            answer.answer
        )
    }

    private makeLocalISOFormattedDateTimeString(date: Date): string {
        let offsetMs = date.getTimezoneOffset() * 60 * 1000
        let msLocal = date.getTime() - offsetMs
        let dateLocal = new Date(msLocal)
        let iso = dateLocal.toISOString()
        return iso
    }

    private adaptMenuLinkTypeDto(dto: MenuLinkTypeDTO): MenuLinkType {
        switch (dto) {
            case MenuLinkTypeDTO.SINGLE_ORGANISER:
                return MenuLinkType.SingleOrganiser
            case MenuLinkTypeDTO.PER_COVER:
                return MenuLinkType.PerCover
        }
    }
}
